import bindAll from 'lodash.bindall';
import React from 'react';

import {connect} from 'react-redux';
import {intlShape, injectIntl} from 'react-intl';

import {
    openSpriteLibrary,
    closeSpriteLibrary
} from '../reducers/modals';

import {activateTab, COSTUMES_TAB_INDEX, BLOCKS_TAB_INDEX} from '../reducers/editor-tab';
import {setReceivedBlocks} from '../reducers/hovered-target';
import {setRestore} from '../reducers/restore-deletion';
import {setAssetUploading} from '../reducers/isAssetUploading';
import DragConstants from '../lib/drag-constants';
import TargetPaneComponent from '../components/target-pane/target-pane.jsx';
import spriteLibraryContent from '../lib/libraries/sprites.json';
import {handleFileUpload, spriteUpload} from '../lib/file-uploader.js';
import {getOssSign, upload, textCheck, toCdnUrl} from '../lib/services/scratch-ser';
import axios from '../lib/services';
import swal from '../lib/swal';
import {queryParams, getUserinfo} from '../lib/services/query-params';
import {getHashParams} from '../lib/query-string';
import sharedMessages from '../lib/shared-messages';
import {emptySprite} from '../lib/empty-assets';
import {highlightTarget} from '../reducers/targets';
import {fetchSprite, fetchCode} from '../lib/backpack-api';
import randomizeSpritePosition from '../lib/randomize-sprite-position';
import {uploadSpriteLimitSize, uploadExceedAlert} from '../lib/upload-limit';

const {role} = queryParams;
const workStatusQuery = getHashParams('workStatus') || queryParams.workStatus;

const getCheckStatus = result => {
    if (result && result.data && result.data.data && result.data.data.status !== undefined) {
        return result.data.data.status;
    }
    if (result && result.data && result.data.status !== undefined) {
        return result.data.status;
    }
    return null;
};

const pollCheck = async params => {
    const startTime = Date.now();
    const timeout = 300000;
    while (Date.now() - startTime < timeout) {
        const result = await axios.post(`${API_BASE_URL}/core/safeCheck/guardian/check`, params);
        const status = getCheckStatus(result);
        if (status === 3 || status === 4 || status === 5) return result;
        if (status !== 1 && status !== 2) return result;
        await new Promise(resolve => window.setTimeout(resolve, 3000));
    }
    throw new Error('guardian/check timeout');
};

const getOssMd5 = async url => {
    try {
        const res = await fetch(url, {method: 'HEAD'});
        const etag = res.headers.get('ETag');
        return etag ? etag.replace(/['"]/g, '').toLowerCase() : null;
    } catch (error) {
        return null;
    }
};

class TargetPane extends React.Component {
    constructor (props) {
        super(props);
        bindAll(this, [
            'handleActivateBlocksTab',
            'handleBlockDragEnd',
            'handleChangeSpriteRotationStyle',
            'handleChangeSpriteDirection',
            'handleChangeSpriteName',
            'handleChangeSpriteSize',
            'handleChangeSpriteVisibility',
            'handleChangeSpriteX',
            'handleChangeSpriteY',
            'handleDeleteSprite',
            'handleDrop',
            'handleDuplicateSprite',
            'handleExportSprite',
            'handleNewSprite',
            'handleSelectSprite',
            'handleSurpriseSpriteClick',
            'handlePaintSpriteClick',
            'handleFileUploadClick',
            'handleSpriteUpload',
            'setFileInput'
        ]);
    }
    componentDidMount () {
        this.props.vm.addListener('BLOCK_DRAG_END', this.handleBlockDragEnd);
    }
    componentWillUnmount () {
        this.props.vm.removeListener('BLOCK_DRAG_END', this.handleBlockDragEnd);
    }
    handleChangeSpriteDirection (direction) {
        this.props.vm.postSpriteInfo({direction});
    }
    handleChangeSpriteRotationStyle (rotationStyle) {
        this.props.vm.postSpriteInfo({rotationStyle});
    }
    handleChangeSpriteName (name) {
        this.props.vm.renameSprite(this.props.editingTarget, name);
    }
    handleChangeSpriteSize (size) {
        this.props.vm.postSpriteInfo({size});
    }
    handleChangeSpriteVisibility (visible) {
        this.props.vm.postSpriteInfo({visible});
    }
    handleChangeSpriteX (x) {
        this.props.vm.postSpriteInfo({x});
    }
    handleChangeSpriteY (y) {
        this.props.vm.postSpriteInfo({y});
    }
    handleDeleteSprite (id) {
        const restoreSprite = this.props.vm.deleteSprite(id);
        const restoreFun = () => restoreSprite().then(this.handleActivateBlocksTab);

        this.props.dispatchUpdateRestore({
            restoreFun: restoreFun,
            deletedItem: 'Sprite'
        });

    }
    handleDuplicateSprite (id) {
        this.props.vm.duplicateSprite(id);
    }
    handleExportSprite (id) {
        const spriteName = this.props.vm.runtime.getTargetById(id).getName();
        const saveLink = document.createElement('a');
        document.body.appendChild(saveLink);

        this.props.vm.exportSprite(id).then(content => {
            const filename = `${spriteName}.sprite3`;

            // Use special ms version if available to get it working on Edge.
            if (navigator.msSaveOrOpenBlob) {
                navigator.msSaveOrOpenBlob(content, filename);
                return;
            }

            const url = window.URL.createObjectURL(content);
            saveLink.href = url;
            saveLink.download = filename;
            saveLink.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(saveLink);
        });
    }
    handleSelectSprite (id) {
        this.props.vm.setEditingTarget(id);
        if (this.props.stage && id !== this.props.stage.id) {
            this.props.onHighlightTarget(id);
        }
    }
    handleSurpriseSpriteClick () {
        const item = spriteLibraryContent[Math.floor(Math.random() * spriteLibraryContent.length)];
        randomizeSpritePosition(item);
        this.props.vm.addSprite(JSON.stringify(item.json))
            .then(this.handleActivateBlocksTab);
    }
    handlePaintSpriteClick () {
        const formatMessage = this.props.intl.formatMessage;
        const emptyItem = emptySprite(
            formatMessage(sharedMessages.sprite, {index: 1}),
            formatMessage(sharedMessages.pop),
            formatMessage(sharedMessages.costume, {index: 1})
        );
        this.props.vm.addSprite(JSON.stringify(emptyItem)).then(() => {
            setTimeout(() => { // Wait for targets update to propagate before tab switching
                this.props.onActivateTab(COSTUMES_TAB_INDEX);
            });
        });
    }
    handleActivateBlocksTab () {
        this.props.onActivateTab(BLOCKS_TAB_INDEX);
    }
    handleNewSprite (spriteJSONString) {
        this.props.vm.addSprite(spriteJSONString)
            .then(this.handleActivateBlocksTab);
    }
    handleFileUploadClick () {
        this.fileInput.click();
    }
    handleSpriteUpload (e) {
        const storage = this.props.vm.runtime.storage;
        const costumeSuffix = this.props.intl.formatMessage(sharedMessages.costume, {index: 1});
        const fileInput = e.target;
        const uploadFile = fileInput.files && fileInput.files[0];
        if (!uploadFile) return;
        const actualSpriteSize = (uploadFile.size / Math.pow(1024, 2)).toFixed(2);
        if (actualSpriteSize >= uploadSpriteLimitSize) {
            fileInput.value = null;
            uploadExceedAlert(uploadSpriteLimitSize, actualSpriteSize);
            return false;
        }
        const readAndUploadSprite = () => {
            handleFileUpload(fileInput, (buffer, fileType, fileName) => {
                spriteUpload(buffer, fileType, fileName, storage, this.handleNewSprite, costumeSuffix);
            });
        };
        const imageFormat = uploadFile.name && uploadFile.name.lastIndexOf('.') > -1 ?
            uploadFile.name.slice(uploadFile.name.lastIndexOf('.')).toLowerCase() : '';
            const sds = workStatusQuery
        this.props.onSetAssetUploading(true);
        getOssSign({
            params: [{
                type: 2,
                fileName: uploadFile.name
            }]
        })
            .then(response => {
                const signData = response.data.data[0].sign;
                const businessId = response.data.data[0].savePath || signData.fileName;
                return upload(uploadFile, signData).then(uploadRes => ({
                    signData,
                    businessId,
                    uploadRes
                }));
            })
            .then(async ({signData, businessId, uploadRes}) => {
                if (!workStatusQuery || !uploadFile.type.startsWith('image/')) {
                    readAndUploadSprite();
                    fileInput.value = null;
                    return;
                }
                const {id: authorId, realName: authorName} = getUserinfo() || {};
                const url = toCdnUrl(signData.dir);
                const fallbackMd5 = ((businessId || signData.fileName || signData.dir || '').split('?')[0].split('/').pop() || '').split('.')[0];
                const etagMd5 = uploadRes && uploadRes.headers && uploadRes.headers.etag ?
                    uploadRes.headers.etag.replace(/['"]/g, '').toLowerCase() : null;
                const md5 = etagMd5 || await getOssMd5(url) || fallbackMd5;
                const textCheckRes = await textCheck({url: uploadFile.name, module: 6});
                if (getCheckStatus(textCheckRes) === 4) {
                    await swal.alert('提示', `素材图片${imageFormat ? `(${imageFormat.replace('.', '').toUpperCase()})` : ''}不符合平台的发布规范，请重新上传`);
                    fileInput.value = null;
                    return;
                }
                const result = await pollCheck({
                    type: 1,
                    url,
                    md5,
                    module: 1,
                    authorId,
                    authorName,
                    role,
                    businessId: businessId || md5
                });
                if (getCheckStatus(result) === 4) {
                    await swal.alert('提示', `素材图片${imageFormat ? `(${imageFormat.replace('.', '').toUpperCase()})` : ''}不符合平台的发布规范，请重新上传`);
                    fileInput.value = null;
                    return;
                }
                readAndUploadSprite();
            })
            .catch(error => {
                fileInput.value = null;
                console.error('sprite image upload check error:', error);
                swal.alert('提示', '图片资源校验失败，请稍后重试');
            })
            .finally(() => {
                this.props.onSetAssetUploading(false);
            });
    }
    setFileInput (input) {
        this.fileInput = input;
    }
    handleBlockDragEnd (blocks) {
        if (this.props.hoveredTarget.sprite && this.props.hoveredTarget.sprite !== this.props.editingTarget) {
            this.props.vm.shareBlocksToTarget(blocks, this.props.hoveredTarget.sprite, this.props.editingTarget);
            this.props.onReceivedBlocks(true);
        }
    }
    handleDrop (dragInfo) {
        const {sprite: targetId} = this.props.hoveredTarget;
        if (dragInfo.dragType === DragConstants.SPRITE && dragInfo.newIndex !== null) {
            // Add one to both new and target index because we are not counting/moving the stage
            this.props.vm.reorderTarget(dragInfo.index + 1, dragInfo.newIndex + 1);
        } else if (dragInfo.dragType === DragConstants.BACKPACK_SPRITE) {
            // TODO storage does not have a way of loading zips right now, and may never need it.
            // So for now just grab the zip manually.
            fetchSprite(dragInfo.payload.bodyUrl)
                .then(sprite3Zip => this.props.vm.addSprite(sprite3Zip));
        } else if (targetId) {
            // Something is being dragged over one of the sprite tiles or the backdrop.
            // Dropping assets like sounds and costumes duplicate the asset on the
            // hovered target. Shared costumes also become the current costume on that target.
            // However, dropping does not switch the editing target or activate that editor tab.
            // This is based on 2.0 behavior, but seems like it keeps confusing switching to a minimum.
            // it allows the user to share multiple things without switching back and forth.
            if (dragInfo.dragType === DragConstants.COSTUME) {
                this.props.vm.shareCostumeToTarget(dragInfo.index, targetId);
            } else if (targetId && dragInfo.dragType === DragConstants.SOUND) {
                this.props.vm.shareSoundToTarget(dragInfo.index, targetId);
            } else if (dragInfo.dragType === DragConstants.BACKPACK_COSTUME) {
                // In scratch 2, this only creates a new sprite from the costume.
                // We may be able to handle both kinds of drops, depending on where
                // the drop happens. For now, just add the costume.
                this.props.vm.addCostume(dragInfo.payload.body, {
                    name: dragInfo.payload.name
                }, targetId);
            } else if (dragInfo.dragType === DragConstants.BACKPACK_SOUND) {
                this.props.vm.addSound({
                    md5: dragInfo.payload.body,
                    name: dragInfo.payload.name
                }, targetId);
            } else if (dragInfo.dragType === DragConstants.BACKPACK_CODE) {
                fetchCode(dragInfo.payload.bodyUrl)
                    .then(blocks => {
                        this.props.vm.shareBlocksToTarget(blocks, targetId);
                        this.props.vm.refreshWorkspace();
                    });
            }
        }
    }
    render () {
        const {
            onActivateTab, // eslint-disable-line no-unused-vars
            onReceivedBlocks, // eslint-disable-line no-unused-vars
            onHighlightTarget, // eslint-disable-line no-unused-vars
            dispatchUpdateRestore, // eslint-disable-line no-unused-vars
            ...componentProps
        } = this.props;
        return (
            <TargetPaneComponent
                {...componentProps}
                fileInputRef={this.setFileInput}
                onActivateBlocksTab={this.handleActivateBlocksTab}
                onChangeSpriteDirection={this.handleChangeSpriteDirection}
                onChangeSpriteName={this.handleChangeSpriteName}
                onChangeSpriteRotationStyle={this.handleChangeSpriteRotationStyle}
                onChangeSpriteSize={this.handleChangeSpriteSize}
                onChangeSpriteVisibility={this.handleChangeSpriteVisibility}
                onChangeSpriteX={this.handleChangeSpriteX}
                onChangeSpriteY={this.handleChangeSpriteY}
                onDeleteSprite={this.handleDeleteSprite}
                onDrop={this.handleDrop}
                onDuplicateSprite={this.handleDuplicateSprite}
                onExportSprite={this.handleExportSprite}
                onFileUploadClick={this.handleFileUploadClick}
                onPaintSpriteClick={this.handlePaintSpriteClick}
                onSelectSprite={this.handleSelectSprite}
                onSpriteUpload={this.handleSpriteUpload}
                onSurpriseSpriteClick={this.handleSurpriseSpriteClick}
            />
        );
    }
}

const {
    onSelectSprite, // eslint-disable-line no-unused-vars
    onActivateBlocksTab, // eslint-disable-line no-unused-vars
    ...targetPaneProps
} = TargetPaneComponent.propTypes;

TargetPane.propTypes = {
    intl: intlShape.isRequired,
    ...targetPaneProps
};

const mapStateToProps = state => ({
    editingTarget: state.scratchGui.targets.editingTarget,
    hoveredTarget: state.scratchGui.hoveredTarget,
    sprites: Object.keys(state.scratchGui.targets.sprites).reduce((sprites, k) => {
        let {direction, size, x, y, ...sprite} = state.scratchGui.targets.sprites[k];
        if (typeof direction !== 'undefined') direction = Math.round(direction);
        if (typeof x !== 'undefined') x = Math.round(x);
        if (typeof y !== 'undefined') y = Math.round(y);
        if (typeof size !== 'undefined') size = Math.round(size);
        sprites[k] = {...sprite, direction, size, x, y};
        return sprites;
    }, {}),
    stage: state.scratchGui.targets.stage,
    raiseSprites: state.scratchGui.blockDrag,
    spriteLibraryVisible: state.scratchGui.modals.spriteLibrary
});
const mapDispatchToProps = dispatch => ({
    onNewSpriteClick: e => {
        e.preventDefault();
        dispatch(openSpriteLibrary());
    },
    onRequestCloseSpriteLibrary: () => {
        dispatch(closeSpriteLibrary());
    },
    onActivateTab: tabIndex => {
        dispatch(activateTab(tabIndex));
    },
    onReceivedBlocks: receivedBlocks => {
        dispatch(setReceivedBlocks(receivedBlocks));
    },
    dispatchUpdateRestore: restoreState => {
        dispatch(setRestore(restoreState));
    },
    onHighlightTarget: id => {
        dispatch(highlightTarget(id));
    },
    onSetAssetUploading: isUploading => {
        dispatch(setAssetUploading(isUploading));
    }
});

export default injectIntl(connect(
    mapStateToProps,
    mapDispatchToProps
)(TargetPane));
